/** Client de la route d'analyse de fichier. */

import { apiFetch, detailOf } from "./http";

export interface AnalysedColumn {
  index: number;
  header: string;
  samples: string[];
  role: string;
  confidence: number;
  rationale: string;
  characteristic_code: string | null;
  datetime_format: string | null;
  is_confident: boolean;
}

export interface AnalysedStructure {
  encoding: string;
  delimiter: string;
  skip_rows: number;
  has_header: boolean;
  decimal_separator: string;
  orientation: string;
  warnings: string[];
  raw_preview: string[];
  source_format: string;
  sheet_name: string | null;
  sheet_names: string[];
}

export interface AnalysedMeasurement {
  characteristic_code: string;
  value: number;
  ts: string;
  context: Record<string, string>;
  source_row: number;
}

export interface AnalysedReject {
  row_number: number;
  reason: string;
  detail: string;
  raw: string;
}

export interface AnalysedChart {
  characteristic_code: string;
  chart_type: string;
  subgroup_size: number;
  points: number[];
  center: number;
  lower: number;
  upper: number;
  violation_indexes: number[];
  violation_labels: string[];
  /** Effectif de la fenêtre d'analyse, avant réduction d'affichage. */
  points_total: number;
  /** Effectif de la série complète, toutes mesures confondues. */
  series_total: number;
  /** Rang de la première mesure de la fenêtre, à partir de 1. */
  window_offset: number;
  /** Ce sur quoi portent les calculs, en clair. Toujours affiché. */
  window_label: string;
  violations_total: number;
  rules_evaluated_on: number;
  /** Moyenne et écart-type de la fenêtre, calculés côté serveur. */
  mean: number;
  sigma: number;
  diagnosis: SeriesDiagnosis | null;
  capability: Capability | null;
}

/**
 * Diagnostic d'autocorrélation.
 *
 * Affiché à côté de la carte, jamais appliqué en silence : ajuster des limites sans le
 * dire produirait un chiffre de plus auquel personne ne comprend rien, alors que nommer
 * le phénomène donne à l'ingénieur quelque chose qu'il peut porter en réunion.
 */
export interface SeriesDiagnosis {
  rho: number;
  /** Intervalle d'échantillonnage qui rendrait la carte valide. */
  lag: number;
  severity: "none" | "notable" | "strong";
  nature: "quality" | "process" | "unknown";
  /** Facteur de resserrement des limites si l'on garde la carte telle quelle. */
  narrowing: number;
  title: string;
  detail: string;
  remedy: string;
  is_misleading: boolean;
}

/** La nature ne change pas la mesure, elle inverse la conclusion : sur un paramètre de
 *  conduite l'autocorrélation est de la physique, sur une cote produit c'est une usure
 *  d'outil ou une dérive de réglage. */
export const NATURE_LABELS: Record<string, string> = {
  quality: "Cote produit",
  process: "Paramètre de conduite",
  unknown: "Nature à préciser",
};

/**
 * Segment de la série sur lequel portent les calculs.
 *
 * Ce n'est pas un réglage d'affichage : limites de contrôle, règles de détection et
 * indices en dépendent tous. Le même objet servira quelle que soit l'origine des
 * données — fichier déposé, dossier surveillé par l'agent, MES ou flux OPC.
 */
export interface Tolerance {
  lsl: number | null;
  usl: number | null;
  target: number | null;
  /** borne -> « fichier » ou « saisie ». Ce qu'un auditeur regardera en premier. */
  origins: Record<string, string>;
  /** borne -> justification lisible. */
  rationales: Record<string, string>;
  /** Message si les bornes se contredisent. La capabilité n'est alors pas calculée. */
  inconsistency: string | null;
}

/** Ce qu'on envoie au serveur pour corriger ou saisir une tolérance. */
export interface ToleranceInput {
  lsl?: number | null;
  usl?: number | null;
  target?: number | null;
}

export interface Capability {
  cp: number | null;
  cpk: number | null;
  pp: number | null;
  ppk: number | null;
  lsl: number | null;
  usl: number | null;
  target: number | null;
  sigma_method: string;
  n_values: number;
  warnings: string[];
}

/** Seuils d'usage. 1,33 est l'attente courante en automobile ; 1,00 signifie que le
 *  procédé occupe exactement sa tolérance, donc qu'un écart quelconque produit un rebut. */
export const CAPABILITY_LEVELS = { good: 1.33, acceptable: 1.0 };

export interface AnalysisWindow {
  mode: "last" | "first" | "range" | "period" | "all";
  size?: number;
  start?: number;
  end?: number;
  from_ts?: string;
  to_ts?: string;
}

/** Tailles proposées. Volontairement peu nombreuses : un menu à quinze entrées fait
 *  hésiter, six couvrent du contrôle courant à l'étude longue. */
export const WINDOW_PRESETS = [30, 50, 100, 150, 300, 1000];

export const DEFAULT_WINDOW: AnalysisWindow = { mode: "last", size: 100 };

/**
 * Ce que le système a reconnu du format, avant que l'utilisateur valide quoi que ce soit.
 *
 * Trois issues, et l'interface ne les traite pas de la même façon :
 *
 * - `connu` — le mapping du projet est **appliqué**, pas seulement proposé. Reconfirmer
 *   trente colonnes chaque matin est une friction qui fait abandonner l'outil ;
 * - `derive` — même projet, format changé : on le dit, on date le changement, et la
 *   série continue. Un firmware qui réordonne les colonnes ne change ni la machine ni
 *   les cotes ;
 * - `inconnu` — l'utilisateur nomme le projet qui recevra ces mesures.
 */
export interface Recognition {
  outcome: "connu" | "derive" | "inconnu";
  score: number;
  project_id: string | null;
  project_name: string;
  template_version: number | null;
  /** Ce qui a changé, en clair : « Colonne(s) apparue(s) : temperature ». */
  differences: string[];
  known_since: string | null;
  /** Vrai quand les rôles enregistrés ont réellement été réappliqués à ce fichier. */
  applied: boolean;
}

export interface AnalyseResult {
  tolerances: Record<string, Tolerance>;
  /** Identifiant du dépôt enregistré, `null` si l'analyse n'a rien écrit. */
  file_id: string | null;
  measurements_stored: number;
  /** Ce contenu exact avait déjà été déposé : rien n'a été dupliqué. */
  already_ingested: boolean;
  filename: string;
  structure: AnalysedStructure;
  columns: AnalysedColumn[];
  notes: string[];
  fingerprint: string;
  uncertain_columns: number[];
  measurements_total: number;
  measurements_preview: AnalysedMeasurement[];
  rejects_total: number;
  rejects_by_reason: Record<string, number>;
  rejects_preview: AnalysedReject[];
  characteristics_found: string[];
  chart: AnalysedChart | null;
  chart_unavailable_reason: string | null;
  charts: AnalysedChart[];
  charts_unavailable: Record<string, string>;
  /** Identifiant de l'analyse conservée côté serveur : changer de fenêtre se fait avec
   *  lui seul, sans renvoyer le fichier. */
  analysis_id: string;
  /** Reconnaissance du format parmi les projets. `null` si la base n'a pas répondu —
   *  l'analyse fonctionne quand même, le questionnaire s'ouvre simplement. */
  recognition: Recognition | null;
  /** Projet dans lequel les mesures ont été écrites. */
  project_id: string | null;
  project_name: string;
  /** Le format de ce projet a changé : nouvelle version du gabarit, série continue. */
  format_changed: boolean;
  template_version: number | null;
  /** L'enregistrement attend un nom de projet. L'analyse ci-dessus est complète : on
   *  montre les mesures d'abord, on demande le nom ensuite. */
  project_required: boolean;
}

/**
 * Corrections apportées par l'utilisateur.
 *
 * Renvoyées au serveur avec le fichier à chaque modification : l'analyse reste sans
 * état côté serveur, donc rien à expirer ni à nettoyer. Le prix est de re-téléverser
 * le fichier, ce qui reste imperceptible sur un aperçu plafonné à 10 Mo.
 */
export interface MappingOverrides {
  tolerances?: Record<string, ToleranceInput>;
  sheet_name?: string;
  orientation?: "long" | "wide";
  decimal_separator?: "." | ",";
  delimiter?: string;
  skip_rows?: number;
  has_header?: boolean;
  roles?: Record<number, string>;
  characteristic_codes?: Record<number, string>;
  datetime_formats?: Record<number, string>;
}

/** Libellés français des rôles. Le back renvoie des identifiants stables, l'interface
 *  les traduit — ainsi renommer un libellé ne casse aucun template enregistré. */
export const ROLE_LABELS: Record<string, string> = {
  value: "Valeur mesurée",
  characteristic: "Nom de caractéristique",
  timestamp: "Date et heure",
  date: "Date",
  time: "Heure",
  lot: "Lot",
  shift: "Équipe",
  operator: "Opérateur",
  machine: "Machine",
  cavity: "Empreinte",
  context: "Axe de stratification",
  tolerance_min: "Tolérance mini (LSL)",
  tolerance_max: "Tolérance maxi (USL)",
  tolerance_target: "Cote nominale / cible",
  serial: "N° de série",
  ignore: "Ignorée",
};

/** Rôles qui désignent une borne de spécification. Une borne ne se suffit pas à
 *  elle-même : il faut savoir ce qu'elle borne. */
export const TOLERANCE_ROLES = [
  "tolerance_min",
  "tolerance_max",
  "tolerance_target",
];

export function isToleranceRole(role: string): boolean {
  return TOLERANCE_ROLES.includes(role);
}

/** Ordre du menu de correction : les rôles structurants d'abord, le rebut en dernier. */
export const ROLE_ORDER: string[] = [
  "value",
  "timestamp",
  "date",
  "time",
  "characteristic",
  "tolerance_min",
  "tolerance_max",
  "tolerance_target",
  "cavity",
  "shift",
  "context",
  "machine",
  "operator",
  "lot",
  "serial",
  "ignore",
];

/** Où déposer. Les deux champs sont exclusifs : on choisit un projet, ou on en nomme un.
 *
 *  Laisser les deux vides confierait le choix à la reconnaissance de format côté serveur.
 *  L'interface ne s'en sert pas pour écrire : elle envoie `useRecognition: false` et
 *  désigne toujours le projet, quitte à recopier celui que la reconnaissance propose.
 *  Un dépôt qui part parce qu'un fichier ressemble à un autre ne se distingue pas, une
 *  fois en base, d'un dépôt que quelqu'un a demandé. Le repli automatique reste au
 *  service des canaux sans écran — agent de terrain, dépôt d'API. */
export interface ProjectTarget {
  projectId?: string | null;
  projectName?: string | null;
  /** Autoriser le serveur à choisir le projet par ressemblance de format. */
  useRecognition?: boolean;
}

export async function analyseFile(
  file: File,
  overrides?: MappingOverrides,
  window?: AnalysisWindow,
  /** Enregistrer le dépôt en base.
   *
   *  Faux par défaut : analyser n'est pas déposer. Le contraire faisait écrire chaque
   *  correction de mapping et chaque saisie de tolérance, sans qu'aucun geste ne l'ait
   *  demandé — l'utilisateur ne pouvait ni savoir que c'était fait, ni s'en abstenir. */
  persist = false,
  target: ProjectTarget = {},
): Promise<AnalyseResult> {
  const body = new FormData();
  body.append("file", file);
  if (overrides && Object.keys(overrides).length > 0) {
    body.append("overrides", JSON.stringify(overrides));
  }
  if (window) {
    body.append("window", JSON.stringify(window));
  }
  body.append("persist", persist ? "true" : "false");
  body.append("use_recognition", target.useRecognition ? "true" : "false");
  if (target.projectId) body.append("project_id", target.projectId);
  if (target.projectName) body.append("project_name", target.projectName);

  const response = await apiFetch("/v1/mapping/analyze", {
    method: "POST",
    body,
  });
  if (!response.ok) {
    throw new Error(await detailOf(response));
  }
  return (await response.json()) as AnalyseResult;
}

/** Levée quand l'analyse conservée côté serveur a expiré. Ce n'est pas une erreur à
 *  montrer : l'appelant renvoie le fichier, l'utilisateur ne voit qu'un délai. */
export class AnalysisExpired extends Error {}

/**
 * Recalcule les cartes sur un autre segment, sans renvoyer le fichier.
 *
 * Quelques octets en entrée contre plusieurs mégaoctets auparavant, et surtout aucune
 * relecture de classeur — c'est la différence entre un clic instantané et quatre
 * minutes d'attente.
 */
export async function changeWindowRemote(
  analysisId: string,
  window: AnalysisWindow,
  natures: Record<string, string> = {},
  tolerances: Record<string, ToleranceInput> = {},
): Promise<
  Pick<
    AnalyseResult,
    "charts" | "charts_unavailable" | "chart_unavailable_reason"
  >
> {
  const response = await apiFetch("/v1/mapping/window", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      analysis_id: analysisId,
      window,
      natures,
      tolerances,
    }),
  });

  if (response.status === 409) throw new AnalysisExpired();
  if (!response.ok) {
    throw new Error(await detailOf(response));
  }
  return (await response.json()) as Pick<
    AnalyseResult,
    "charts" | "charts_unavailable" | "chart_unavailable_reason"
  >;
}
